import { type FundingSourceDTO } from '@/domain/fundingSource/types';
import { type BasicOkr, type OKR } from '@/domain/okrs/type';
import { type PlantBasicDTO } from '@/domain/plant/types';
import { type OKRAdd, type ProductBrief, type UnitAdd } from '@/domain/products/types';
import { type RoadmapResource } from '@/domain/resources/types';
import {
  type BasicRoadmapStatusDTO,
  type BasicRoadmapStatusItemsDTO,
  type BasicRoadmapType,
  type RoadMapType,
} from '@/domain/roadmapType/types';
import { type UnitImpact } from '@/domain/units/types';
import { type BasicUserDTO, type UserBrief } from '@/domain/user/types';

import type { GetRoadmapTempoDTO } from './tempo.interfaces';

export interface RoadmapQueryState {
  isFetching: boolean;
  isLoading: boolean;
  isPlaceholderData: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export interface RoadmapFollower {
  name: string;
  email: string;
  idRoadmap: number;
  idRoadmapFollower: number;
}

export interface RoadmapFundingSource {
  idRoadmapFundingSource: number;
  idFundingSource: number;
  fundingSource: FundingSourceDTO;
  percentage: number;
  amount?: number;
}

export interface RoadmapStatusResource {
  idRoadmapStatusResource: number;
  idRoadmap: number;
  idRoadmapStatus: number;
  idRoadmapResource: number;
  hours: number;
  startDate: string;
  endDate: string;
  resource: RoadmapResource;
}

export interface StatusResource extends RoadmapResource {
  idRoadmapStatusResource: number;
  idRoadmap: number;
  hours: number;
  idResource: number | null;
}

export interface RoadmapStatusWithResourceDTO extends RoadmapStatus {
  edited: boolean;
}

export type MilestoneStatus = 'Planned' | 'Development';

export enum ItemTrackingStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  Completed = 'Completed',
  Delayed = 'Delayed',
  NotApplicable = 'Not Applicable',
}

export interface CommentDto {
  idComment: number;
  idUser: number;
  comment: string;
  timestamp: string;
  user: UserBrief;
  edited?: boolean;
}

export interface RoadmapStatusItem extends BasicRoadmapStatusItemsDTO {
  status: ItemTrackingStatus;
  edited: boolean;
  comments?: CommentDto[];
  percentComplete: number;
  qualityAssurance: boolean;
}

export interface RoadmapStatus extends BasicRoadmapStatusDTO {
  idRoadmapType: number;
  type: string;
  edited: boolean;
  startDate?: string;
  endDate?: string;
  newStartDate?: string;
  newEndDate?: string;
  roadmapStatusItems: RoadmapStatusItem[];
  resources?: StatusResource[];
}

export interface PlatformVersionDTO extends BasicPlatformVersionDTO {
  release: PlatformRelease;
}

export interface PlatformRelease {
  idPlatformRelease: number;
  idPlatform: number;
  name: string;
  platform?: RoadmapPlatform;
}

export interface GroupedBasic {
  selectedIds: number[];
  name: string;
}

export interface RoadmapPlatform {
  idPlatform: number;
  name: string;
}

export interface RoadmapDefaultFilters {
  platformId: number | null;
  platformName: string | null;
  releaseId: number | null;
  releaseName: string | null;
}

export interface Resource {
  idRoadmapResource: number;
  hourlyRate: number;
  hours: number;
  resource: string;
}

export interface RoadmapMilestone {
  idRoadmapMilestone: number;
  idRoadmap: number;
  milestone: string;
  status: string;
  resources: Resource[];
}

export interface BasePlant {
  idPlant: number;
  plant: string;
}

export interface Plant {
  idPlant: number;
  plant: string;
  idCountry: number;
  idIndustry: number;
  linkWebCrm: string;
}

export interface RoadmapPlant {
  idRoadmapPlant: number;
  idRoadmap: number;
  idPlant: number;
  comment: string;
  plant: Plant;
}

export interface RoadmapListProductIndustry {
  idIndustry: number;
  idIndustryArea?: number;
  idManager?: number;
}

export interface RoadmapListProduct {
  idProduct: number;
  product: string;
  idPlatform?: number;
  productGroup: string;
  idTechnologyManager?: number;
  idProductManager?: number;
  qaSpecialistsIds?: number[];
  productOwner?: BasicUserDTO;
  industries: RoadmapListProductIndustry[];
  productUnitId: number;
}

export interface BasicPlatformVersionDTO {
  idPlatformVersion: number;
  idPlatformRelease: number;
  name: string;
  releaseDate: string;
  namePlatformRelease: string;
}

export interface RoadmapListStatus {
  idRoadmapStatus: number;
  name: string;
  startDate?: string;
  endDate?: string;
  isRelease: boolean;
}

export interface BasicFundingSourceDTO {
  idFundingSource: number;
}

export interface BasicFundingSource {
  idFundingSource: number;
  percentage: number;
  name: string;
  alias?: string;
}

export interface RoadmapList extends RoadmapListDetail {
  okrs: BasicOkr[];
  unitsImpact: UnitImpact[];
  linkedItems: RoadmapItemLink[];
  externalOrigin: string;
}

export interface RoadmapListDetail {
  idRoadmap: number;
  item: string;
  percentComplete: number;
  qualityAssurance: number;
  cost: number;
  hours?: number;
  actualMinutes?: number;
  product: RoadmapListProduct;
  version?: BasicPlatformVersionDTO;
  status?: RoadmapListStatus;
  type: BasicRoadmapType;
  owner?: BasicUserDTO;
  fundingSources: BasicFundingSource[];
  origin?: number;
  idOwner?: number;
  locked: boolean;
  isParticipant: boolean;
  isFollower: boolean;
  idReporter?: number;
  portfolioId?: number;
  createdById?: number;
  resourceIds: number[];
  resourceGroupIds: number[];
  responsibleIds: number[];
  category: number;
  priority: number;
  requestingBusinessArea?: string;
}

export interface RoadmapTempoListDetail extends RoadmapListDetail {
  participantsIds: number[];
  tempoAllocated: GetRoadmapTempoDTO[];
}

export interface RoadmapFundingSourceList extends Omit<RoadmapList, 'fundingSources'> {
  fundingSources: RoadmapFundingSource[];
  resources: RoadmapStatusResource[];
}

export interface RoadMapMinimalDTO {
  item: string;
  description: string;
}

export interface RoadMap {
  idRoadmap: number;
  idProduct: number;
  idRoadmapVersion: number;
  itemChangesCount: number;
  versionChangesCount: number;
  idOwner?: number;
  idReporter?: number;
  createdById?: number;
  item: string;
  description: string;
  detailedDescription: string;
  type: RoadMapType | null;
  status: RoadmapStatus;
  createdTimestamp: string;
  updatedTimestamp: string;
  specification: string;
  qualityCertificate: string;
  locked: boolean;
  product: ProductBrief;
  priority: RoadmapPriority;
  classification: number;
  category: number;
  portfolioId?: number;
  origin?: 0 | 1;
  milestones: RoadmapMilestone[];
  plants: RoadmapPlant[];
  okrs: OKR[];
  version?: PlatformVersionDTO;
  plannedHours: number | null;
  percentComplete: number;
  qualityAssurance: number;
  owner?: UserBrief;
  fundingSources: RoadmapFundingSource[];
  cost: number;
  resources: RoadmapStatusResource[];
  createdBy?: string;
  responsibleIds: number[];
  unitsImpact: UnitImpact[];
  linkedItems: RoadmapItemLink[];
  externalOrigin: string;
  jiraTicket?: string;
}

export type RoadmapPriority = 0 | 1 | 2 | 3;

export interface RoadMapUpdate {
  idRoadmap: number;
  idProduct: number;
  idRoadmapVersion?: number;
  itemChangesCount: number;
  versionChangesCount: number;
  idOwner?: number;
  item: string;
  description: string;
  detailedDescription: string;
  status: RoadmapStatus | null;
  specification: string;
  qualityCertificate: string;
  product: ProductBrief | null;
  okrs: OKRAdd[];
  milestones: RoadmapMilestone[];
  plants: number[];
  version: PlatformVersionDTO | null;
  owner: UserBrief | null;
  type: RoadMapType | null;
  fundingSources: RoadmapFundingSource[];
  createdTimestamp?: string;
  createdBy?: string;
  affectedVersions?: PlatformVersionDTO[];
  fixVersions?: PlatformVersionDTO[];
  priority: RoadmapPriority;
  classification: number;
  category: number;
  portfolioId?: number;
  plannedHours: number | null;
  origin?: 0 | 1;
  reporter?: UserBrief;
  participants?: UserBrief[];
  customers?: PlantBasicDTO[];
  responsibleIds: number[];
  locked: boolean;
  unitsImpact: UnitAdd[];
  externalOrigin: string;
  linkedItems: RoadmapItemLinkAdd[];
  jiraTicket?: string;
}

export interface History {
  idRoadmapHistory: number;
  timestamp: string;
  field: string;
  originalValue: string;
  newValue: string;
  user: UserBrief;
}

export interface RoadmapStatusItemHistoryDTO {
  idRoadmapStatusTrackingHistory: number;
  idRoadmap: number;
  field: string;
  originalValue: string;
  newValue: string;
  roadmapStatusItem: string;
  timestamp: string;
  user?: UserBrief;
}

export type RoadmapStatusResourceHistoryAction = 'Created' | 'Updated' | 'Deleted';

export interface RoadmapStatusResourceHistoryDTO {
  idRoadmapStatusResourceHistory: number;
  phase: string;
  hours: number | null;
  resourceId: number | null;
  resourceGroupId: number | null;
  roadmapId: number;
  action: RoadmapStatusResourceHistoryAction;
  timestamp: string;
  user: UserBrief;
}

export interface RoadmapStatusSimpleDTO {
  idRoadmapStatus: number | null;
  idRoadmapType: number;
  name: string;
  statusOrder: number;
}

export interface RoadmapClassification {
  label: string;
  value: number;
}

export interface RoadmapCategory {
  label: string;
  value: number;
}

export interface BasicRoadmap {
  idRoadmap: number;
  item: string;
  isParticipant: boolean;
  isOwner: boolean;
  isReporter: boolean;
  isCreator: boolean;
}

export interface ReleaseViewModel {
  id: number;
  release: string;
  okr: string;
  pims: string;
  apc: string;
  advancedDataAnalytics: string;
  controlSystem: string;
  digitalTwins: string;
  operationPerformanceManagement: string;
  productivity: string;
  metrisUXPlatform: string;
  roadmaps: RoadmapViewModel[];
}

export interface RoadmapViewModel {
  id: number;
  product: string;
  productGroup: string;
  item: string;
  version: string;
  date: string | null;
}

export interface RoadmapLinkType {
  id: number;
  name: string;
}

export interface RoadmapItemLink {
  id: number;
  sourceRoadmapId: number;
  targetRoadmapId: number;
  roadmapLinkTypeId: number;
}

export interface RoadmapItemLinkAdd {
  targetRoadmapId: number;
  roadmapLinkTypeId: number;
}
